import React from 'react';
import { IoSearchOutline, IoFilterOutline, IoDownloadOutline, IoPencilOutline, IoTrashOutline, IoChevronDown, IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { FaThermometerHalf } from 'react-icons/fa';
import { useFoodTemperature } from '../context/FoodTemperatureContext';
import { getAssetUrl } from '../../../../components/AssetUrl';

const formatTime = (time) => {
    if (!time) return '—';
    const parsed = new Date(time);
    return Number.isNaN(parsed.getTime()) ? time : parsed.toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
};

export default function TodaysTemperatureLogTable() {
    const { logs: tableData, loading, error, isToday, formattedDate } = useFoodTemperature();

    return (
        <div className="w-full  bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">

            {/* Top Controls Toolbar */}
            <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100">

                {/* Left Side: Title & Badge */}
                <div className="flex items-center gap-2.5">
                    <h2 className="text-lg font-bold text-gray-900">{isToday ? "Today's Temperature Log" : `Temperature Log — ${formattedDate}`}</h2>
                    <span className="px-2 py-0.5 text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-full">
                        {tableData.length} {tableData.length === 1 ? 'entry' : 'entries'}
                    </span>
                </div>

                {/* Right Side: Search, Filter, Export Actions */}
                <div className="flex flex-wrap items-center gap-2">
                    {/* Search Bar */}
                    <div className="relative w-full sm:w-60">
                        <input
                            type="text"
                            placeholder="Search food items..."
                            className="w-full pl-9 pr-4 py-2 text-sm text-gray-800 bg-gray-50/50 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:bg-white transition-all placeholder-gray-400"
                        />
                        <IoSearchOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
                    </div>

                    {/* Filter Dropdown Button */}
                    <button className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                        <IoFilterOutline className="text-base" />
                        <span>Filter</span>
                        <IoChevronDown className="text-gray-400 text-xs ml-1" />
                    </button>

                    {/* Export Button */}
                    <button className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                        <IoDownloadOutline className="text-base" />
                        <span>Export</span>
                    </button>
                </div>

            </div>

            {/* Main Data Table Wrapper for Horizontal Scrolling */}
            <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[900px]">
                    <thead>
                        <tr className="bg-gray-50/40 border-b border-gray-100">
                            <th className="px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider w-20">Time</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Food Item</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Type</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Initial Temp (°C)</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Final Temp (°C)</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Action Taken</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Status</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider w-16">Photo</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-center w-24">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {tableData.length === 0 && (
                            <tr>
                                <td colSpan={9} className="px-5 py-8 text-center text-sm text-gray-500" role="status">
                                    {loading ? 'Loading temperature logs...' : error ? `Unable to load temperature logs: ${error}` : isToday ? 'No temperature entries recorded today.' : 'No temperature entries recorded for this date.'}
                                </td>
                            </tr>
                        )}
                        {tableData.map((row, index) => (
                            <tr key={index} className="hover:bg-gray-50/30 transition-colors">
                                {/* Time */}
                                <td className="px-5 py-3.5 text-sm font-medium text-gray-500 whitespace-nowrap">
                                    {formatTime(row.time)}
                                </td>

                                {/* Food Item */}
                                <td className="px-5 py-3.5 text-sm font-bold text-gray-800 whitespace-nowrap">
                                    {row.foodItemName}
                                </td>

                                {/* Type With Reactive Color Temperature Badge Icon */}
                                <td className="px-5 py-3.5 text-sm font-semibold whitespace-nowrap">
                                    <div className="flex items-center gap-1.5 text-gray-700">
                                        <FaThermometerHalf className={`text-xs ${row.type === 'Hot Holding' ? 'text-rose-500' : 'text-blue-500'}`} />
                                        <span>{row.type}</span>
                                    </div>
                                </td>

                                {/* Initial Temp */}
                                <td className="px-5 py-3.5 text-sm font-medium text-gray-600 whitespace-nowrap">
                                    {row.initialTemp}
                                </td>

                                {/* Final Temp */}
                                <td className="px-5 py-3.5 text-sm font-medium text-gray-600 whitespace-nowrap">
                                    {row.finalTemp}
                                </td>

                                {/* Action Taken */}
                                <td className="px-5 py-3.5 text-sm font-medium whitespace-nowrap">
                                    <span className={row.actionTaken ? 'text-gray-800 font-semibold' : 'text-gray-400'}>
                                        {row.actionTaken || 'None'}
                                    </span>
                                </td>

                                {/* Status Badges */}
                                <td className="px-5 py-3.5 whitespace-nowrap">
                                    {row.status === 'Within Range' ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold bg-emerald-50 text-emerald-600 border border-emerald-100">
                                            Within Range
                                        </span>
                                    ) : row.status === 'Out of Range' ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold bg-rose-50 text-rose-500 border border-rose-100 animate-pulse">
                                            Out of Range
                                        </span>
                                    ) : <span className="text-xs text-gray-400">Not recorded</span>}
                                </td>

                                {/* Photo Thumbnail */}
                                <td className="px-5 py-3.5 whitespace-nowrap">
                                    {row.photoUrl ? <img
                                        src={getAssetUrl(row.photoUrl)}
                                        alt={row.foodItemName}
                                        className="w-10 h-10 object-cover rounded-lg border border-gray-100 bg-gray-50 shadow-inner"
                                    /> : <span className="text-gray-400">—</span>}
                                </td>

                                {/* Edit / Delete Icons */}
                                <td className="px-5 py-3.5 whitespace-nowrap text-center">
                                    <div className="flex items-center justify-center gap-1.5">
                                        <button className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
                                            <IoPencilOutline className="text-base" />
                                        </button>
                                        <button className="p-1.5 rounded-lg text-gray-400 hover:text-rose-500 hover:bg-rose-50 transition-all">
                                            <IoTrashOutline className="text-base" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination Footer Controls */}
            <div className="p-4 bg-white border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Record Entries Statistics Count */}
                <span className="text-xs font-semibold text-gray-400">
                    Showing {tableData.length > 0 ? 1 : 0} to {tableData.length} of {tableData.length} entries
                </span>

                {/* Navigation Actions Controls block */}
                <div className="flex items-center gap-4">
                    {/* Pagination Page Selectors arrows numbers row */}
                    <div className="flex items-center gap-1">
                        <button disabled className="p-2 text-gray-300 cursor-not-allowed rounded-lg">
                            <IoChevronBack className="text-sm" />
                        </button>
                        <button className="w-8 h-8 flex items-center justify-center text-sm font-bold text-emerald-600 bg-white border border-emerald-500 rounded-lg shadow-sm">
                            1
                        </button>
                        <button disabled className="p-2 text-gray-300 cursor-not-allowed rounded-lg">
                            <IoChevronForward className="text-sm" />
                        </button>
                    </div>

                    {/* Custom Select Per Page Count Element */}
                    <div className="relative">
                        <select className="appearance-none pl-3 pr-8 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-indigo-500 transition-all cursor-pointer">
                            <option>10 / page</option>
                            <option>25 / page</option>
                            <option>50 / page</option>
                        </select>
                        <IoChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
                    </div>
                </div>

            </div>

        </div>
    );
}

